export const normalizePhoneValue = (value) => String(value ?? '').replace(/\s+/g, '').trim();

const isPhoneLike = (value) => {
  const normalized = normalizePhoneValue(value);
  if (!normalized || !/^\+?\d+$/.test(normalized)) {
    return false;
  }

  return normalized.replace(/\D/g, '').length >= 7;
};

const normalizeEmployeeLookupValue = (value) => String(value ?? '').trim().toLowerCase();

const buildEmployeeNameCandidates = (name) => {
  const normalizedName = normalizeEmployeeLookupValue(name);
  if (!normalizedName) {
    return [];
  }

  const candidates = [normalizedName];
  if (normalizedName.includes('_')) {
    candidates.push(normalizedName.replace(/_/g, '/'));
  }

  return [...new Set(candidates)];
};

const stripLastExtension = (value) => String(value ?? '').replace(/\.[^.]+$/, '');

const parseVirtualPbxFilename = (filename) => {
  if (!filename) return null;
  const basename = filename.split('/').pop();
  const match = /^(\d{4})-(\d{2})-(\d{2})__(\d{2})-(\d{2})-(\d{2})__(.+)$/.exec(
    basename
  );

  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second, fieldsRaw] = match;
  const fields = fieldsRaw.split('__');
  if (fields.length < 2) {
    return null;
  }

  const firstField = fields[0].trim();
  const secondField = stripLastExtension(fields[1]).trim();

  if (isPhoneLike(firstField)) {
    return {
      callStartedAt: `${year}-${month}-${day}T${hour}:${minute}:${second}`,
      callDirection: 'incoming',
      employeeName: secondField,
      clientPhone: normalizePhoneValue(firstField)
    };
  }

  if (!isPhoneLike(secondField)) {
    return null;
  }

  return {
    callStartedAt: `${year}-${month}-${day}T${hour}:${minute}:${second}`,
    callDirection: 'outgoing',
    employeeName: firstField,
    clientPhone: normalizePhoneValue(secondField)
  };
};

export const parseCallMetaFromFilename = (filename) => {
  if (!filename) return null;
  const virtualPbxMeta = parseVirtualPbxFilename(filename);
  if (virtualPbxMeta) {
    return virtualPbxMeta;
  }

  const basename = filename.split('/').pop();
  const match = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-UTC_(in|out)(?=(_|\.|$))/i.exec(
    basename
  );

  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second, direction] = match;
  const directionMap = {
    in: 'incoming',
    out: 'outgoing'
  };

  return {
    callStartedAt: `${year}-${month}-${day}T${hour}:${minute}:${second}`,
    callDirection: directionMap[direction.toLowerCase()]
  };
};

export const matchEmployeeByName = (name, employees) => {
  const candidates = buildEmployeeNameCandidates(name);
  if (candidates.length === 0 || !Array.isArray(employees) || employees.length === 0) {
    return null;
  }

  const exactMatch = employees.find((employee) =>
    candidates.includes(normalizeEmployeeLookupValue(employee?.fullName))
  );
  if (exactMatch?.id) {
    return exactMatch.id;
  }

  const firstWordMatch = employees.find((employee) => {
    const firstWord = String(employee?.fullName ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)[0];
    return candidates.includes(normalizeEmployeeLookupValue(firstWord));
  });

  return firstWordMatch?.id ?? null;
};
